(function () {
  if (customElements.get('product-info-popup')) return;

  const nutritionRequests = new Map();
  const laboratoryRequests = new Map();
  class ProductInfoPopup extends HTMLElement {
    connectedCallback() {
      if (this.dataset.popupInitialized === 'true') return;
      this.dataset.popupInitialized = 'true';

      this.trigger = this.querySelector('.product-info-popup__trigger');
      this.dialog = this.querySelector('.product-info-popup__dialog');
      this.closeButton = this.querySelector('.product-info-popup__close');
      if (!this.trigger || !this.dialog) return;

      this.formatDirections();
      this.updateLaboratoryVisibility();

      this.trigger.addEventListener('click', () => this.open());
      this.closeButton?.addEventListener('click', () => this.close());
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });
      this.dialog.addEventListener('close', () => this.afterClose());
    }

    updateLaboratoryVisibility() {
      if (!this.classList.contains('product-info-popup--laboratory')) return;
      this.hidden = !this.trigger.querySelector('[data-laboratory-available]');
      if (this.hidden && this.dialog.open) this.close();
    }

    formatDirections() {
      const content = this.querySelector('.product-info-popup__content--directions');
      if (!content || content.querySelector('ol')) return;

      const steps = [];
      content.querySelectorAll(':scope > p').forEach((paragraph) => {
        paragraph.innerHTML
          .split(/(?:<br\s*\/?>(?:\s|&nbsp;)*){2,}/i)
          .map((part) => part.trim())
          .filter(Boolean)
          .forEach((part) => steps.push(part));
      });
      if (steps.length === 0) return;

      const list = document.createElement('ol');
      steps.forEach((step) => {
        const item = document.createElement('li');
        item.innerHTML = step;
        list.appendChild(item);
      });
      content.replaceChildren(list);
    }

    open() {
      if (this.hidden || this.dialog.open) return;
      this.dialog.showModal();
      document.body.classList.add('product-info-popup-open');
      window.theme?.scrollLock?.lock('product-info-popup');
      window.qlfInitInfoTables?.(this.dialog);
      this.loadNutrition();
      this.loadLaboratory();
      this.closeButton?.focus();
    }

    async loadNutrition() {
      const loader = this.querySelector('[data-nutrition-loader]');
      if (
        !loader ||
        loader.dataset.loaded === 'true' ||
        loader.getAttribute('aria-busy') === 'true'
      )
        return;

      const status = loader.querySelector('[data-nutrition-status]');
      const retry = loader.querySelector('[data-nutrition-retry]');
      retry.onclick = () => this.loadNutrition();
      retry.hidden = true;
      status.textContent = 'Nährwerte & Zutaten werden geladen …';
      loader.setAttribute('aria-busy', 'true');

      // Keep preview/localization parameters and key the cache by product + variant.
      const url = new URL(loader.dataset.productUrl, window.location.origin);
      const pageParams = new URLSearchParams(window.location.search);
      ['preview_theme_id', '_fd', 'pb', 'locale'].forEach((name) => {
        if (pageParams.has(name)) url.searchParams.set(name, pageParams.get(name));
      });
      url.searchParams.set('variant', loader.dataset.variantId);
      url.searchParams.set('section_id', 'qlf-nutrition-table');
      const key = url.toString();
      try {
        if (!nutritionRequests.has(key)) {
          const request = fetch(key)
            .then((response) => {
              if (!response.ok) throw new Error('Nutrition request failed');
              return response.text();
            })
            .then((html) => {
              const doc = new DOMParser().parseFromString(html, 'text/html');
              const content = doc.querySelector('[data-nutrition-content]');
              if (!content || content.textContent.includes('Liquid error')) {
                throw new Error('Invalid nutrition section');
              }
              return content.innerHTML;
            })
            .catch((error) => {
              nutritionRequests.delete(key);
              throw error;
            });
          nutritionRequests.set(key, request);
        }
        const html = await nutritionRequests.get(key);
        // A variant change can replace the loader while the request is in flight.
        if (!loader.isConnected) return;
        loader.innerHTML = html;
        loader.dataset.loaded = 'true';
        if (!loader.querySelector('.nutritional-table')) {
          loader.textContent = 'Für diese Auswahl sind keine Nährwerte & Zutaten hinterlegt.';
        }
        window.qlfInitInfoTables?.(loader);
      } catch (error) {
        status.textContent = 'Nährwerte & Zutaten konnten nicht geladen werden.';
        retry.hidden = false;
      } finally {
        loader.setAttribute('aria-busy', 'false');
      }
    }

    async loadLaboratory() {
      const loader = this.querySelector('[data-laboratory-loader]');
      if (!loader || loader.dataset.loaded === 'true' || loader.getAttribute('aria-busy') === 'true') return;

      const status = loader.querySelector('[data-laboratory-status]');
      const retry = loader.querySelector('[data-laboratory-retry]');
      retry.onclick = () => this.loadLaboratory();
      retry.hidden = true;
      status.textContent = 'Laboranalyse wird geladen …';
      loader.setAttribute('aria-busy', 'true');

      const url = new URL(loader.dataset.productUrl, window.location.origin);
      const pageParams = new URLSearchParams(window.location.search);
      ['preview_theme_id', '_fd', 'pb', 'locale'].forEach((name) => {
        if (pageParams.has(name)) url.searchParams.set(name, pageParams.get(name));
      });
      // Keep the certificate and request cache specific to the selected variant.
      url.searchParams.set('variant', loader.dataset.variantId);
      url.searchParams.set('section_id', 'qlf-laboratory-popup');
      const key = url.toString();
      try {
        if (!laboratoryRequests.has(key)) {
          const request = fetch(key)
            .then((response) => {
              if (!response.ok) throw new Error('Laboratory request failed');
              return response.text();
            })
            .then((html) => {
              const doc = new DOMParser().parseFromString(html, 'text/html');
              const content = doc.querySelector('[data-laboratory-content]');
              if (!content || !content.innerHTML.trim() || content.textContent.includes('Liquid error')) {
                throw new Error('Invalid laboratory section');
              }
              return content.innerHTML;
            })
            .catch((error) => {
              laboratoryRequests.delete(key);
              throw error;
            });
          laboratoryRequests.set(key, request);
        }
        const html = await laboratoryRequests.get(key);
        if (!loader.isConnected) return;
        loader.innerHTML = html;
        loader.dataset.loaded = 'true';
        if (loader.querySelector('[data-laboratory-unavailable]')) {
          this.hidden = true;
          this.close();
        }
      } catch (error) {
        if (!loader.isConnected) return;
        status.textContent = 'Die Laboranalyse konnte nicht geladen werden.';
        retry.hidden = false;
      } finally {
        loader.setAttribute('aria-busy', 'false');
      }
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    afterClose() {
      document.body.classList.remove('product-info-popup-open');
      window.theme?.scrollLock?.unlock('product-info-popup');
      if (!this.hidden) this.trigger?.focus();
    }
  }

  document.addEventListener('product:variant-rendered', () => {
    document.querySelectorAll('product-info-popup').forEach((popup) => {
      popup.updateLaboratoryVisibility();
      if (popup.dialog?.open) {
        popup.loadNutrition();
        popup.loadLaboratory();
      }
    });
  });

  customElements.define('product-info-popup', ProductInfoPopup);
})();
